/**
 * useSleepPlan Hook
 *
 * Core hook for the Sleep Plan feature.
 * Manages sleep plan settings, calculates wake window state,
 * and interfaces with the baby_care_entries sleep data.
 *
 * v2: Adds three-phase mode management (observe/learn/active),
 * adaptive algorithm integration, and feedback support.
 *
 * Shares sleep tracking with Baby-Care-Koordinator:
 * - Starting sleep timer uses the same baby_care_entries table
 * - Sleep plan reads the same activeSleepSessions from useBabyCare
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useFeatureEnabled } from '@/hooks/useFeatureFlags';
import { useLimits } from '@/hooks/useLimits';
import { useBabyCare } from '@/features/baby-care';
import { isValidUUID } from '@/lib/utils';
import {
  calculateWakeWindowState,
  getCorrectedAgeWeeks,
  getWakeWindowRange,
  type WakeWindowRange,
  type WakeWindowState,
} from '../lib/wakeWindowCalculator';
import {
  calculateNotificationSchedule,
  scheduleWakeWindowNotifications,
  cancelSleepPlanNotifications,
} from '../lib/sleepNotifications';
import {
  type SleepPlanMode,
  type PersonalizedWakeWindow,
  type SleepCycleScore,
  calculatePersonalizedWakeWindow,
  checkModeTransition,
  ADAPTIVE_WINDOW_DAYS,
  type ModeTransitionCheck,
} from '../lib/sleepScoring';
import {
  saveSleepFeedback,
  loadCycleScores,
  type SaveFeedbackParams,
} from '../services/sleepFeedbackService';

export interface SleepPlanSettings {
  id: string;
  pregnancyId: string;
  babyIndex: number;
  isActive: boolean;
  mode: SleepPlanMode;
  prematureWeeks: number;
  notifyUser1: boolean;
  notifyUser2: boolean;
  quietHoursStart: string;
  quietHoursEnd: string;
  notifyEarlyWarning: boolean;
  notifyWindowOpens: boolean;
  notifyWindowCloses: boolean;
  notifyOvertired: boolean;
  lastWakeTime: string | null;
  totalCyclesTracked: number;
  totalCyclesWithFeedback: number;
  autoUpgradeMode: boolean;
  /** Gespeicherter Snapshot des persoenlichen Fensters (steuert die Push-Planung). */
  personalizedWindowMin: number | null;
  personalizedWindowMax: number | null;
}

export interface SleepPlanData {
  ageWeeks: number;
  correctedAgeWeeks: number;
  mode: SleepPlanMode;
  wakeWindow: WakeWindowRange | null;
  lastWakeTime: Date | null;
  minutesAwake: number | null;
  todayTotalSleepMinutes: number;
  todayNapCount: number;
  avgNapDurationMinutes: number;
  isBabySleeping: boolean;
  sleepCues: Array<{ signal: string; stage: 'early' | 'middle' | 'late' }>;
  tips: string[];
  totalCyclesTracked: number;
  totalCyclesWithFeedback: number;
  canUpgradeToLearn: boolean;
  canUpgradeToActive: boolean;
}

export interface SleepAnalysisData {
  periodDays: number;
  avgWakeWindowMinutes: number | null;
  avgNapDurationMinutes: number | null;
  avgNightSleepHours: number | null;
  avgNapsPerDay: number | null;
  dailyData: Array<{
    date: string;
    totalSleepMinutes: number;
    napCount: number;
    nightSleepHours: number;
  }>;
}

export interface SleepPatternCluster {
  avgStartMinutes: number;
  avgEndMinutes: number;
  avgDurationMinutes: number;
  entryCount: number;
  daysPresent: number;
  totalDays: number;
  sleepType: 'nap' | 'night';
}

export interface SleepPatternData {
  clusters: SleepPatternCluster[];
  totalEntries: number;
  periodDays: number;
}

export interface UseSleepPlanReturn {
  isFeatureEnabled: boolean;
  isPremium: boolean;
  isLoading: boolean;
  settings: SleepPlanSettings | null;
  data: SleepPlanData | null;
  wakeWindowState: WakeWindowState | null;
  analysisData: SleepAnalysisData | null;
  personalizedWindow: PersonalizedWakeWindow | null;
  modeTransition: ModeTransitionCheck | null;

  patternData: SleepPatternData | null;
  /** Number of usable cycle scores in the last 14 days (for adaptive algorithm) */
  recentCycleCount: number;

  // Actions
  activateSleepPlan: (prematureWeeks?: number, mode?: SleepPlanMode, autoUpgrade?: boolean) => Promise<void>;
  deactivateSleepPlan: () => Promise<void>;
  updateSettings: (updates: Partial<SleepPlanSettings>) => Promise<void>;
  changeMode: (newMode: SleepPlanMode) => Promise<void>;
  refreshData: () => Promise<void>;
  loadAnalysis: (days: number) => Promise<void>;
  loadPatternClusters: (days?: number) => Promise<void>;
  submitFeedback: (params: Omit<SaveFeedbackParams, 'pregnancyId' | 'babyIndex'>) => Promise<void>;

  // Shared with Baby-Care
  activeSleepSessions: ReturnType<typeof useBabyCare>['activeSleepSessions'];
  startSleepTimer: (pregnancyId: string, babyIndex: number) => Promise<void>;
  endSleepSession: ReturnType<typeof useBabyCare>['endSleepSession'];
  cancelSleepSession: ReturnType<typeof useBabyCare>['cancelSleepSession'];
}

export function useSleepPlan(babyIndex: number = 0): UseSleepPlanReturn {
  const isFeatureEnabled = useFeatureEnabled('sleep_plan_enabled');
  const { userTier } = useLimits();
  const isPremium = userTier === 'premium';
  const { lastCompletedPregnancy } = usePregnancyStore();

  const rawPregnancyId = lastCompletedPregnancy?.id;
  const pregnancyId = isValidUUID(rawPregnancyId) ? rawPregnancyId : null;
  const birthDate = lastCompletedPregnancy?.actual_birth_date
    ? new Date(lastCompletedPregnancy.actual_birth_date)
    : null;

  // Get shared baby care functions
  const {
    activeSleepSessions,
    addEntry,
    endSleepSession,
    cancelSleepSession,
  } = useBabyCare();

  // Wrap addEntry as a sleep-start helper for the Sleep Plan UI
  const startSleepTimer = useCallback(async (sleepPregnancyId: string, babyIndex: number) => {
    await addEntry({
      pregnancy_id: sleepPregnancyId,
      care_type: 'sleep',
      baby_index: babyIndex,
      recorded_at: new Date().toISOString(),
    });
  }, [addEntry]);

  const [settings, setSettings] = useState<SleepPlanSettings | null>(null);
  const [data, setData] = useState<SleepPlanData | null>(null);
  const [analysisData, setAnalysisData] = useState<SleepAnalysisData | null>(null);
  const [patternData, setPatternData] = useState<SleepPatternData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch settings on mount
  useEffect(() => {
    if (!pregnancyId) {
      setIsLoading(false);
      return;
    }

    async function loadSettings() {
      const { data: settingsData, error } = await supabase
        .from('sleep_plan_settings')
        .select('*')
        .eq('pregnancy_id', pregnancyId)
        .eq('baby_index', babyIndex)
        .maybeSingle();

      if (error) {
        console.error('Error loading sleep plan settings:', error);
        setIsLoading(false);
        return;
      }

      if (settingsData) {
        setSettings({
          id: settingsData.id,
          pregnancyId: settingsData.pregnancy_id,
          babyIndex: settingsData.baby_index,
          isActive: settingsData.is_active,
          mode: settingsData.mode ?? 'observe',
          prematureWeeks: settingsData.premature_weeks ?? 0,
          notifyUser1: settingsData.notify_user_1 ?? true,
          notifyUser2: settingsData.notify_user_2 ?? true,
          quietHoursStart: settingsData.quiet_hours_start ?? '22:00',
          quietHoursEnd: settingsData.quiet_hours_end ?? '06:00',
          notifyEarlyWarning: settingsData.notify_early_warning ?? true,
          notifyWindowOpens: settingsData.notify_window_opens ?? true,
          notifyWindowCloses: settingsData.notify_window_closes ?? true,
          notifyOvertired: settingsData.notify_overtired ?? true,
          lastWakeTime: settingsData.last_wake_time,
          totalCyclesTracked: settingsData.total_cycles_tracked ?? 0,
          totalCyclesWithFeedback: settingsData.total_cycles_with_feedback ?? 0,
          autoUpgradeMode: settingsData.auto_upgrade_mode ?? false,
          personalizedWindowMin: settingsData.personalized_window_min ?? null,
          personalizedWindowMax: settingsData.personalized_window_max ?? null,
        });
      }

      setIsLoading(false);
    }

    loadSettings();
  }, [pregnancyId, babyIndex]);

  // Load sleep plan data via RPC
  const loadData = useCallback(async () => {
    if (!pregnancyId || !birthDate) return;

    try {
      const { data: rpcData, error } = await supabase.rpc('get_sleep_plan_data', {
        p_pregnancy_id: pregnancyId,
        p_baby_index: babyIndex,
      });

      if (error) {
        console.error('Error loading sleep plan data:', error);
        // Fall back to client-side calculation
        const correctedAgeWeeks = getCorrectedAgeWeeks(birthDate, settings?.prematureWeeks ?? 0);
        const windowRange = getWakeWindowRange(correctedAgeWeeks);
        setData({
          ageWeeks: getCorrectedAgeWeeks(birthDate, 0),
          correctedAgeWeeks,
          mode: settings?.mode ?? 'observe',
          wakeWindow: windowRange,
          lastWakeTime: null,
          minutesAwake: null,
          todayTotalSleepMinutes: 0,
          todayNapCount: 0,
          avgNapDurationMinutes: 0,
          isBabySleeping: activeSleepSessions.length > 0,
          sleepCues: [],
          tips: [],
          totalCyclesTracked: 0,
          totalCyclesWithFeedback: 0,
          canUpgradeToLearn: false,
          canUpgradeToActive: false,
        });
        return;
      }

      if (rpcData) {
        const ww = rpcData.wake_window;
        setData({
          ageWeeks: rpcData.age_weeks,
          correctedAgeWeeks: rpcData.corrected_age_weeks,
          mode: rpcData.mode ?? settings?.mode ?? 'observe',
          wakeWindow: ww
            ? { minMinutes: ww.min_minutes, maxMinutes: ww.max_minutes, napsMin: ww.naps_min, napsMax: ww.naps_max }
            : getWakeWindowRange(rpcData.corrected_age_weeks),
          lastWakeTime: rpcData.last_wake_time ? new Date(rpcData.last_wake_time) : null,
          minutesAwake: rpcData.minutes_awake != null ? Math.floor(rpcData.minutes_awake) : null,
          todayTotalSleepMinutes: rpcData.today_total_sleep_minutes ?? 0,
          todayNapCount: rpcData.today_nap_count ?? 0,
          avgNapDurationMinutes: rpcData.avg_nap_duration_minutes ?? 0,
          isBabySleeping: rpcData.is_baby_sleeping ?? activeSleepSessions.length > 0,
          sleepCues: ww?.sleep_cues ?? [],
          tips: ww?.tips ?? [],
          totalCyclesTracked: rpcData.total_cycles_tracked ?? 0,
          totalCyclesWithFeedback: rpcData.total_cycles_with_feedback ?? 0,
          canUpgradeToLearn: rpcData.can_upgrade_to_learn ?? false,
          canUpgradeToActive: rpcData.can_upgrade_to_active ?? false,
        });
      }
    } catch (err) {
      console.error('Error in loadData:', err);
    }
  }, [pregnancyId, birthDate, settings?.prematureWeeks, activeSleepSessions.length, babyIndex]);

  // Load data when settings are active
  useEffect(() => {
    if (settings?.isActive && pregnancyId) {
      loadData();
    }
  }, [settings?.isActive, pregnancyId, loadData]);

  // Refresh data every 30 seconds when active + not sleeping
  useEffect(() => {
    if (!settings?.isActive || !data || data.isBabySleeping) return;

    const interval = setInterval(loadData, 30_000);
    return () => clearInterval(interval);
  }, [settings?.isActive, data?.isBabySleeping, loadData]);

  // Adaptive algorithm: load cycle scores and compute personalized window
  const [cycleScores, setCycleScores] = useState<SleepCycleScore[]>([]);

  // Load cycle scores in all modes so personalization builds up continuously
  useEffect(() => {
    if (!pregnancyId || !settings?.isActive) return;

    loadCycleScores(pregnancyId, babyIndex, ADAPTIVE_WINDOW_DAYS).then(setCycleScores).catch(console.error);
  }, [pregnancyId, babyIndex, settings?.isActive, settings?.mode]);

  const personalizedWindow = useMemo<PersonalizedWakeWindow | null>(() => {
    if (!data?.wakeWindow || !settings?.isActive) return null;
    return calculatePersonalizedWakeWindow(cycleScores, data.wakeWindow);
  }, [data?.wakeWindow, cycleScores, settings?.isActive, settings?.mode]);

  // Persist personalized window to DB so the server-side notification
  // scheduler can use adaptive values in both learn and active modes.
  //
  // Der Effect feuert bei jeder Aenderung des berechneten Fensters, also
  // ohne Zutun des Nutzers und potenziell im 30-Sekunden-Takt des
  // Daten-Refreshs. Schlaegt der Write strukturell fehl - fehlende Spalte
  // nach einer nicht angewendeten Migration, entzogenes Recht -, dann
  // schlaegt er auch beim naechsten Mal fehl. Nach dem ersten Fehlschlag
  // pro Plan wird deshalb nicht weiter geschrieben, sonst produziert eine
  // einzelne Sitzung eine Dauerschleife aus 400ern im Log.
  const personalizedWriteFailedFor = useRef<string | null>(null);
  const lastPersistedWindow = useRef<string | null>(null);

  useEffect(() => {
    if (!settings?.id || !personalizedWindow) return;
    if (personalizedWriteFailedFor.current === settings.id) return;

    const planId = settings.id;

    // Faellt isPersonalized auf false, muss der gespeicherte Snapshot geloescht
    // werden. Sonst zeigt die App das Referenzfenster an, waehrend der
    // serverseitige Scheduler weiter nach den alten Werten plant - UI und Push
    // wuerden sich widersprechen, und der Snapshot wuerde nie aufgeraeumt.
    const targetMin = personalizedWindow.isPersonalized ? personalizedWindow.rangeMin : null;
    const targetMax = personalizedWindow.isPersonalized ? personalizedWindow.rangeMax : null;
    const targetConfidence = personalizedWindow.isPersonalized ? personalizedWindow.confidence : null;

    // Nichts zu loeschen, wenn der Snapshot ohnehin schon leer ist.
    if (targetMin === null && settings.personalizedWindowMin == null && settings.personalizedWindowMax == null) {
      return;
    }

    // Derselbe Wert wird nicht erneut geschrieben. Ohne diese Sperre
    // schreibt jeder 30-Sekunden-Refresh eine wirkungslose Aenderung.
    const payloadKey = `${planId}:${targetMin}:${targetMax}:${targetConfidence}`;
    if (lastPersistedWindow.current === payloadKey) return;
    lastPersistedWindow.current = payloadKey;

    supabase
      .from('sleep_plan_settings')
      .update({
        personalized_window_min: targetMin,
        personalized_window_max: targetMax,
        personalized_confidence: targetConfidence,
        updated_at: new Date().toISOString(),
      })
      .eq('id', planId)
      .then(({ error }) => {
        if (error) {
          personalizedWriteFailedFor.current = planId;
          lastPersistedWindow.current = null;
          console.error(
            'Error persisting personalized window - further attempts skipped for this plan:',
            error,
          );
          return;
        }
        // Lokalen Stand nachziehen, damit die Loesch-Abkuerzung oben greift.
        setSettings(prev =>
          prev && prev.id === planId
            ? { ...prev, personalizedWindowMin: targetMin, personalizedWindowMax: targetMax }
            : prev,
        );
      });
  }, [
    settings?.id,
    settings?.personalizedWindowMin,
    settings?.personalizedWindowMax,
    personalizedWindow?.isPersonalized,
    personalizedWindow?.rangeMin,
    personalizedWindow?.rangeMax,
    personalizedWindow?.confidence,
  ]);

  // Calculate wake window state – use personalized window when available
  const effectiveWindow = useMemo<WakeWindowRange | null>(() => {
    if (!data?.wakeWindow) return null;
    if (personalizedWindow?.isPersonalized) {
      return {
        minMinutes: personalizedWindow.rangeMin,
        maxMinutes: personalizedWindow.rangeMax,
        napsMin: data.wakeWindow.napsMin,
        napsMax: data.wakeWindow.napsMax,
      };
    }
    return data.wakeWindow;
  }, [data?.wakeWindow, personalizedWindow, settings?.mode]);

  const wakeWindowState = useMemo<WakeWindowState | null>(() => {
    if (!effectiveWindow || data?.minutesAwake == null || data?.isBabySleeping) return null;
    return calculateWakeWindowState(data.minutesAwake, effectiveWindow);
  }, [effectiveWindow, data?.minutesAwake, data?.isBabySleeping]);

  // Schedule notifications when wake window state changes
  // No push notifications in observe or learn mode (learn = soft mode, in-app only)
  useEffect(() => {
    if (!settings?.isActive || !effectiveWindow || !data?.lastWakeTime || data.isBabySleeping) {
      return;
    }
    if (settings.mode === 'observe' || settings.mode === 'learn') {
      cancelSleepPlanNotifications().catch(console.error);
      return;
    }

    const schedule = calculateNotificationSchedule(
      data.lastWakeTime,
      effectiveWindow,
      settings.quietHoursStart,
      settings.quietHoursEnd,
      {
        earlyWarning: settings.notifyEarlyWarning,
        windowOpens: settings.notifyWindowOpens,
        windowCloses: settings.notifyWindowCloses,
        overtired: settings.notifyOvertired,
      },
    );

    // Schedule notifications (no-op if all null)
    scheduleWakeWindowNotifications(schedule, 'Baby', {
      earlyWarning: { title: '', body: '' },
      windowOpen: { title: '', body: '' },
      windowClosing: { title: '', body: '' },
      overtired: { title: '', body: '' },
    }).catch(console.error);

    // Note: actual translated titles/bodies are injected at the component level
  }, [data?.lastWakeTime, effectiveWindow, data?.isBabySleeping, settings]);

  // Mode transition check
  const modeTransition = useMemo<ModeTransitionCheck | null>(() => {
    if (!settings || !data) return null;
    return checkModeTransition(settings.mode, data.correctedAgeWeeks, data.totalCyclesWithFeedback);
  }, [settings?.mode, data?.correctedAgeWeeks, data?.totalCyclesWithFeedback]);

  // Auto-upgrade mode when enabled
  useEffect(() => {
    if (!settings?.autoUpgradeMode || !modeTransition || !settings?.isActive) return;

    if (settings.mode === 'observe' && modeTransition.canUpgradeToLearn) {
      changeMode('learn');
    } else if (settings.mode === 'learn' && modeTransition.canUpgradeToActive) {
      changeMode('active');
    }
  }, [settings?.autoUpgradeMode, settings?.mode, settings?.isActive, modeTransition]);

  // Activate sleep plan
  const activateSleepPlan = useCallback(async (prematureWeeks: number = 0, mode: SleepPlanMode = 'observe', autoUpgrade: boolean = false) => {
    if (!pregnancyId) return;

    const { data: result, error } = await supabase
      .from('sleep_plan_settings')
      .upsert({
        pregnancy_id: pregnancyId,
        baby_index: babyIndex,
        is_active: true,
        mode,
        mode_changed_at: new Date().toISOString(),
        activated_at: new Date().toISOString(),
        premature_weeks: prematureWeeks,
        auto_upgrade_mode: autoUpgrade,
      }, {
        onConflict: 'pregnancy_id,baby_index',
      })
      .select()
      .single();

    if (error) {
      console.error('Error activating sleep plan:', error);
      return;
    }

    if (result) {
      setSettings({
        id: result.id,
        pregnancyId: result.pregnancy_id,
        babyIndex: result.baby_index,
        isActive: true,
        mode: result.mode ?? 'observe',
        prematureWeeks: result.premature_weeks ?? 0,
        notifyUser1: result.notify_user_1 ?? true,
        notifyUser2: result.notify_user_2 ?? true,
        quietHoursStart: result.quiet_hours_start ?? '22:00',
        quietHoursEnd: result.quiet_hours_end ?? '06:00',
        notifyEarlyWarning: result.notify_early_warning ?? true,
        notifyWindowOpens: result.notify_window_opens ?? true,
        notifyWindowCloses: result.notify_window_closes ?? true,
        notifyOvertired: result.notify_overtired ?? true,
        lastWakeTime: result.last_wake_time,
        totalCyclesTracked: result.total_cycles_tracked ?? 0,
        totalCyclesWithFeedback: result.total_cycles_with_feedback ?? 0,
        autoUpgradeMode: result.auto_upgrade_mode ?? false,
        personalizedWindowMin: result.personalized_window_min ?? null,
        personalizedWindowMax: result.personalized_window_max ?? null,
      });
    }
  }, [pregnancyId, babyIndex]);

  // Deactivate sleep plan
  const deactivateSleepPlan = useCallback(async () => {
    if (!settings?.id) return;

    const { error } = await supabase
      .from('sleep_plan_settings')
      .update({ is_active: false })
      .eq('id', settings.id);

    if (error) {
      console.error('Error deactivating sleep plan:', error);
      return;
    }

    await cancelSleepPlanNotifications();
    setSettings(prev => prev ? { ...prev, isActive: false } : null);
  }, [settings?.id]);

  // Change mode
  const changeMode = useCallback(async (newMode: SleepPlanMode) => {
    if (!settings?.id) return;

    const { error } = await supabase
      .from('sleep_plan_settings')
      .update({
        mode: newMode,
        mode_changed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', settings.id);

    if (error) {
      console.error('Error changing sleep plan mode:', error);
      return;
    }

    setSettings(prev => prev ? { ...prev, mode: newMode } : null);
    // Reload data to reflect mode change
    loadData();
  }, [settings?.id, loadData]);

  // Submit sleep feedback
  const submitFeedback = useCallback(async (params: Omit<SaveFeedbackParams, 'pregnancyId' | 'babyIndex'>) => {
    if (!pregnancyId) return;

    await saveSleepFeedback({
      ...params,
      pregnancyId,
      babyIndex,
    });

    // Reload cycle scores to update personalized window
    if (settings?.mode !== 'observe') {
      loadCycleScores(pregnancyId, babyIndex, ADAPTIVE_WINDOW_DAYS).then(setCycleScores).catch(console.error);
    }

    // Reload data to reflect updated counters
    loadData();
  }, [pregnancyId, babyIndex, settings?.mode, loadData]);

  // Update settings
  const updateSettings = useCallback(async (updates: Partial<SleepPlanSettings>) => {
    if (!settings?.id) return;

    const dbUpdates: Record<string, unknown> = {};
    if (updates.prematureWeeks !== undefined) dbUpdates.premature_weeks = updates.prematureWeeks;
    if (updates.notifyUser1 !== undefined) dbUpdates.notify_user_1 = updates.notifyUser1;
    if (updates.notifyUser2 !== undefined) dbUpdates.notify_user_2 = updates.notifyUser2;
    if (updates.quietHoursStart !== undefined) dbUpdates.quiet_hours_start = updates.quietHoursStart;
    if (updates.quietHoursEnd !== undefined) dbUpdates.quiet_hours_end = updates.quietHoursEnd;
    if (updates.notifyEarlyWarning !== undefined) dbUpdates.notify_early_warning = updates.notifyEarlyWarning;
    if (updates.notifyWindowOpens !== undefined) dbUpdates.notify_window_opens = updates.notifyWindowOpens;
    if (updates.notifyWindowCloses !== undefined) dbUpdates.notify_window_closes = updates.notifyWindowCloses;
    if (updates.notifyOvertired !== undefined) dbUpdates.notify_overtired = updates.notifyOvertired;
    if (updates.autoUpgradeMode !== undefined) dbUpdates.auto_upgrade_mode = updates.autoUpgradeMode;

    dbUpdates.updated_at = new Date().toISOString();

    const { error } = await supabase
      .from('sleep_plan_settings')
      .update(dbUpdates)
      .eq('id', settings.id);

    if (error) {
      console.error('Error updating sleep plan settings:', error);
      return;
    }

    setSettings(prev => prev ? { ...prev, ...updates } : null);
  }, [settings?.id]);

  // Load analysis data
  const loadAnalysis = useCallback(async (days: number) => {
    if (!pregnancyId) return;

    try {
      const { data: rpcData, error } = await supabase.rpc('get_sleep_analysis', {
        p_pregnancy_id: pregnancyId,
        p_baby_index: babyIndex,
        p_days: days,
      });

      if (error) {
        console.error('Error loading sleep analysis:', error);
        return;
      }

      if (rpcData) {
        setAnalysisData({
          periodDays: rpcData.period_days,
          avgWakeWindowMinutes: rpcData.avg_wake_window_minutes,
          avgNapDurationMinutes: rpcData.avg_nap_duration_minutes,
          avgNightSleepHours: rpcData.avg_night_sleep_hours,
          avgNapsPerDay: rpcData.avg_naps_per_day,
          dailyData: (rpcData.daily_data ?? []).map((d: { date: string; total_sleep_minutes: number; nap_count: number; night_sleep_hours: number }) => ({
            date: d.date,
            totalSleepMinutes: d.total_sleep_minutes,
            napCount: d.nap_count,
            nightSleepHours: d.night_sleep_hours,
          })),
        });
      }
    } catch (err) {
      console.error('Error in loadAnalysis:', err);
    }
  }, [pregnancyId, babyIndex]);

  // Load sleep pattern clusters for the 24h circle visualization
  const loadPatternClusters = useCallback(async (days: number = 14) => {
    if (!pregnancyId) return;

    try {
      const { data: rpcData, error } = await supabase.rpc('get_sleep_pattern_clusters', {
        p_pregnancy_id: pregnancyId,
        p_baby_index: babyIndex,
        p_days: days,
      });

      if (error) {
        console.error('Error loading sleep pattern clusters:', error);
        return;
      }

      if (rpcData) {
        setPatternData({
          clusters: (rpcData.clusters ?? []).map((c: { avg_start_minutes: number; avg_end_minutes: number; avg_duration_minutes: number; entry_count: number; days_present: number; total_days: number; sleep_type: string }) => ({
            avgStartMinutes: c.avg_start_minutes,
            avgEndMinutes: c.avg_end_minutes,
            avgDurationMinutes: c.avg_duration_minutes,
            entryCount: c.entry_count,
            daysPresent: c.days_present,
            totalDays: c.total_days,
            sleepType: c.sleep_type as 'nap' | 'night',
          })),
          totalEntries: rpcData.total_entries,
          periodDays: rpcData.period_days,
        });
      }
    } catch (err) {
      console.error('Error in loadPatternClusters:', err);
    }
  }, [pregnancyId, babyIndex]);

  return {
    isFeatureEnabled,
    isPremium,
    isLoading,
    settings,
    data,
    wakeWindowState,
    analysisData,
    patternData,
    personalizedWindow,
    modeTransition,
    // Bewusst ohne Rueckfall auf totalCyclesWithFeedback: der Lebenszeit-
    // Zaehler beschreibt etwas anderes als die aktuelle Datendichte. Der
    // fruehere Fallback liess den Fortschrittsbalken bei leerem Zeitfenster
    // auf 100% springen, obwohl gar keine Personalisierung aktiv war.
    recentCycleCount: cycleScores.length,
    activateSleepPlan,
    deactivateSleepPlan,
    updateSettings,
    changeMode,
    refreshData: loadData,
    loadAnalysis,
    loadPatternClusters,
    submitFeedback,
    activeSleepSessions,
    startSleepTimer,
    endSleepSession,
    cancelSleepSession,
  };
}
